import { Link } from '@tanstack/react-router';
import {
  type ColumnFiltersState,
  createColumnHelper,
  type OnChangeFn,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import {
  Loader2,
  Mail,
  MoreHorizontal,
  Tags,
  Ticket,
  Trash,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useResendOnboardingEmailMutation } from '~entities/parent';
import {
  type BulkDeleteParentResult,
  useBulkDeleteParent,
} from '~entities/parent/api/bulk-delete-parent';
import { useGetParentByStudio } from '~entities/parent/api/get-parent-by-studio';

import { formatPhoneForDisplay } from '~/shared/utils/format-phone-display';
import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import {
  areAllCurrentPageRowsSelected,
  getSelectedRowIds,
  mergeSelectionCache,
  resolveSelectedRows,
} from '~shared/components/table/resolve-selected-rows';
import { useFilterPersistence } from '~shared/hooks/useFilterPersistence';
import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent } from '~shared/shadcn/ui/card';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

import { BulkAddTagDialog } from './bulk-add-tag-dialog';
import {
  BulkSyncTagFromCrmDialog,
  BulkSyncTagFromCrmToolbarButton,
} from './bulk-sync-tag-from-crm-dialog';
import { DeleteParentDialog } from './delete-parent-dialog';
import { SendTicketsDialog } from './send-tickets-dialog';
import { type ParentTableRow } from './types';

const columnHelper = createColumnHelper<ParentTableRow>();

const PARENT_SORT_KEY_MAP: Record<string, string> = {
  name: 'name',
  familyName: 'familyName',
  primaryPhone: 'primaryPhone',
  email: 'email',
};

export function ParentsListTable() {
  const resendOnboardingEmailMutation = useResendOnboardingEmailMutation();
  const selectedParentsByIdRef = useRef<Record<string, ParentTableRow>>({});

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) => {
              table.toggleAllPageRowsSelected(!!value);
            }}
            aria-label='Select all'
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => {
              row.toggleSelected(!!value);
            }}
            aria-label='Select row'
          />
        ),
        enableSorting: false,
        enableHiding: false,
      }),
      columnHelper.accessor('name', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Parent Name'
          />
        ),

        cell: (info) => (
          <div className='px-4 capitalize'>{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor('familyName', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Family Name'
          />
        ),
        cell: (info) => (
          <Button
            variant={'link'}
            className='capitalize text-custom-secondary-1'
          >
            <Link
              to={`/family`}
              search={{
                parentId: info.row.original._id,
              }}
            >
              {info.getValue()}
            </Link>
          </Button>
        ),
      }),
      columnHelper.accessor('primaryPhone', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            className='pl-4 text-black'
            title='Phone'
          />
        ),

        cell: (info) => (
          <div className='px-4'>{formatPhoneForDisplay(info.getValue())}</div>
        ),
      }),
      columnHelper.accessor('email', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Email'
            className='pl-4 text-black'
          />
        ),
        cell: (info) => <div className='px-4'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('students', {
        enableSorting: false,
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Students'
            className='pl-4 text-black'
          />
        ),
        cell: (info) => {
          const students = info.getValue(); // || [];
          return (
            <div className='space-y-1 px-4'>
              {students.map((student) => (
                <div key={student._id} className='flex items-center gap-2'>
                  <Button
                    variant={'link'}
                    className='h-auto p-0 capitalize text-custom-secondary-1'
                  >
                    <Link
                      to={`/students/$studentId/details`}
                      params={{
                        studentId: student._id,
                      }}
                    >
                      {student.name}
                    </Link>
                  </Button>
                </div>
              ))}
            </div>
          );
        },
      }),
      columnHelper.accessor((row) => row, {
        id: 'actions',
        header: () => (
          <div className='pl-4 font-semibold text-black'>Actions</div>
        ),
        cell: (info) => {
          return (
            <div className='px-4 text-right'>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='ghost' className='size-8 p-0'>
                    <span className='sr-only'>Open menu</span>
                    <MoreHorizontal className='size-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  <DropdownMenuItem
                    onClick={() => {
                      resendOnboardingEmailMutation.mutate(
                        {
                          parentId: info.row.original._id,
                        },
                        {
                          onSuccess: () => {
                            toast.success('Onboarding email sent successfully');
                          },
                          onError: (error) => {
                            toast.error('Failed to send onboarding email');
                            console.error(
                              'Resend onboarding email error:',
                              error,
                            );
                          },
                        },
                      );
                    }}
                    disabled={resendOnboardingEmailMutation.isPending}
                  >
                    {resendOnboardingEmailMutation.isPending ? (
                      <Loader2 className='mr-2 size-4 animate-spin' />
                    ) : (
                      <Mail className='mr-2 size-4' />
                    )}
                    {resendOnboardingEmailMutation.isPending
                      ? 'Sending...'
                      : 'Resend Onboarding Email'}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className='text-red-600'
                    onClick={() => {
                      // Row delete must leave select-all-matching. Otherwise
                      // the dialog still sends the current filters and can
                      // delete every matching parent (ENR-3337 Dev QA).
                      setSelectAllMatching(false);
                      setRowSelection({});
                      selectedParentsByIdRef.current = {};
                      setSelectedParents([info.row.original]);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Trash className='mr-2 size-4' />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      }),
    ],
    [resendOnboardingEmailMutation],
  );

  const { filters, pagination, updateFilter, updatePagination } =
    useFilterPersistence({
      query: '',
      tag: 'All',
    });

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const primarySort = sorting[0];
  const sortBy = primarySort
    ? (PARENT_SORT_KEY_MAP[primarySort.id] ?? primarySort.id)
    : undefined;
  const sortOrder = sortBy ? (primarySort?.desc ? 'desc' : 'asc') : undefined;

  const handleSortingChange = useCallback(
    (updater: React.SetStateAction<SortingState>) => {
      setSorting((prev) => {
        const nextSortedState =
          typeof updater === 'function' ? updater(prev) : updater;
        updatePagination((prevPagination) => ({
          ...prevPagination,
          pageIndex: 0,
        }));
        return nextSortedState;
      });
    },
    [updatePagination],
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkAddTagDialogOpen, setBulkAddTagDialogOpen] = useState(false);
  const [bulkSyncTagFromCrmOpen, setBulkSyncTagFromCrmOpen] = useState(false);
  const [sendTicketsDialogOpen, setSendTicketsDialogOpen] = useState(false);
  const [selectedParents, setSelectedParents] = useState<Array<ParentTableRow>>(
    [],
  );
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const bulkDeleteMutation = useBulkDeleteParent();

  const tagFilter = filters.tag !== 'All' ? filters.tag : undefined;

  const { data: apiData, isFetching } = useGetParentByStudio({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: filters.query,
    sortBy,
    sortOrder,
    tags: tagFilter,
  });

  // Transform the API data to match ParentTableRow type
  const tableData: Array<ParentTableRow> = useMemo(
    () =>
      apiData?.data.map((parent) => ({
        ...parent,
        name: `${parent.firstName} ${parent.lastName}`,
      })) ?? [],
    [apiData?.data],
  );

  const totalMatching = apiData?.pagination.total ?? 0;
  const allPageRowsSelected = areAllCurrentPageRowsSelected(
    tableData,
    rowSelection,
  );
  const selectedCount = selectAllMatching
    ? totalMatching
    : getSelectedRowIds(rowSelection).length;

  selectedParentsByIdRef.current = mergeSelectionCache(
    selectedParentsByIdRef.current,
    tableData,
    rowSelection,
  );
  const selectedParentsFromState = resolveSelectedRows(
    rowSelection,
    tableData,
    selectedParentsByIdRef.current,
  );

  const ignoreSelectAllPageSyncRef = useRef(false);

  const clearListSelection = useCallback(() => {
    // Drop selection in the same event as the filter change. Otherwise the
    // page-sync effect still sees selectAllMatching=true (Bugbot: stale
    // keepPreviousData ids get written back into rowSelection).
    ignoreSelectAllPageSyncRef.current = true;
    setSelectAllMatching(false);
    setRowSelection({});
    selectedParentsByIdRef.current = {};
  }, []);

  // Backup if search/tag changes from persistence rather than the inputs.
  useEffect(() => {
    clearListSelection();
  }, [filters.query, filters.tag, clearListSelection]);

  // Select-all-matching must check the new page's rows too, or unchecking one
  // row cannot leave that mode cleanly. Skip the commit that just cleared
  // filters so keepPreviousData cannot restore the previous result set.
  useEffect(() => {
    if (ignoreSelectAllPageSyncRef.current) {
      ignoreSelectAllPageSyncRef.current = false;
      return;
    }
    if (!selectAllMatching || tableData.length === 0) {
      return;
    }
    setRowSelection((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const parent of tableData) {
        if (!next[parent._id]) {
          next[parent._id] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [selectAllMatching, tableData]);

  // Unchecking a row or the page checkbox exits select-all-matching mode so
  // the delete only ever targets what is actually checked.
  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = (updater) => {
    const next =
      typeof updater === 'function' ? updater(rowSelection) : updater;
    if (selectAllMatching && !areAllCurrentPageRowsSelected(tableData, next)) {
      setSelectAllMatching(false);
    }
    setRowSelection(next);
  };

  const reportBulkDeleteResult = useCallback(
    (result: BulkDeleteParentResult) => {
      const skipped = result.skipped;
      const deletedCount = result.deletedCount;
      if (skipped.length === 0) {
        toast.success(
          `Deleted ${deletedCount} ${deletedCount === 1 ? 'parent' : 'parents'}`,
        );
        return;
      }
      const skippedNames = skipped.map((p) => p.name).filter(Boolean);
      toast.warning(
        `Deleted ${deletedCount} ${deletedCount === 1 ? 'parent' : 'parents'}; skipped ${skipped.length}`,
        {
          description: `${skippedNames.slice(0, 5).join(', ')}${
            skippedNames.length > 5
              ? ` and ${skippedNames.length - 5} more`
              : ''
          } — skipped parents have active enrollments, outstanding balances, or payments in progress.`,
          duration: 10000,
        },
      );
    },
    [],
  );

  return (
    <Card>
      <CardContent className='pt-6'>
        {!selectAllMatching &&
          allPageRowsSelected &&
          totalMatching > tableData.length && (
            <div className='mb-4 flex items-center justify-center gap-2 rounded-md border border-custom-secondary-1/30 bg-custom-secondary-1/10 px-4 py-2 text-sm text-black'>
              <span>
                All {tableData.length} parents on this page are selected.
              </span>
              <Button
                variant='link'
                size='sm'
                className='h-auto p-0 text-custom-secondary-1'
                onClick={() => {
                  setSelectAllMatching(true);
                }}
              >
                Select all {totalMatching} parents matching the current filters
              </Button>
            </div>
          )}
        {selectAllMatching && (
          <div className='mb-4 flex items-center justify-center gap-2 rounded-md border border-custom-secondary-1/30 bg-custom-secondary-1/10 px-4 py-2 text-sm text-black'>
            <span>
              All {totalMatching} parents matching the current filters are
              selected.
            </span>
            <Button
              variant='link'
              size='sm'
              className='h-auto p-0 text-custom-secondary-1'
              onClick={() => {
                setSelectAllMatching(false);
                setRowSelection({});
                selectedParentsByIdRef.current = {};
              }}
            >
              Clear selection
            </Button>
          </div>
        )}
        <div>
          <EnrollioTable
            ariaLabel='Parents'
            totalLabel='Parents'
            filterHeader={
              <>
                <EnrollioTableSearch
                  placeholder='Search by name or email...'
                  value={filters.query}
                  onChange={(value) => {
                    clearListSelection();
                    updateFilter('query', value);
                  }}
                />
                <Select
                  value={filters.tag}
                  onValueChange={(value) => {
                    clearListSelection();
                    updateFilter('tag', value);
                  }}
                >
                  <SelectTrigger className='w-full capitalize md:w-[160px]'>
                    <SelectValue placeholder='Tags' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='All'>All Tags</SelectItem>
                    {(apiData?.tags ?? []).map((tag) => (
                      <SelectItem
                        key={tag._id}
                        value={tag.fieldName}
                        className='capitalize'
                      >
                        {tag.fieldName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            }
            toolbar={(table) => (
              <>
                <BulkSyncTagFromCrmToolbarButton
                  onClick={() => setBulkSyncTagFromCrmOpen(true)}
                />
                {(selectedCount > 0 || selectAllMatching) && (
                  <>
                    {!selectAllMatching && (
                      <>
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={() => {
                            setSelectedParents(selectedParentsFromState);
                            setSendTicketsDialogOpen(true);
                          }}
                        >
                          <Ticket className='mr-2 size-4' />
                          Send Tickets Link ({selectedCount})
                        </Button>
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={() => {
                            setSelectedParents(selectedParentsFromState);
                            setBulkAddTagDialogOpen(true);
                          }}
                        >
                          <Tags className='mr-2 size-4' />
                          Add Tag ({selectedCount})
                        </Button>
                      </>
                    )}
                    <Button
                      variant='destructive'
                      size='sm'
                      onClick={() => {
                        if (selectAllMatching) {
                          setSelectedParents([]);
                        } else {
                          setSelectedParents(selectedParentsFromState);
                        }
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash className='mr-2 size-4' />
                      Delete Selected ({selectedCount})
                    </Button>
                  </>
                )}
                <EnrollioTableColumnToggle table={table} />
              </>
            )}
            columns={columns}
            data={tableData}
            sorting={sorting}
            rowCount={apiData?.pagination.total ?? 0}
            isUpdating={isFetching}
            onSortingChange={handleSortingChange}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            rowSelection={rowSelection}
            onRowSelectionChange={handleRowSelectionChange}
            pagination={pagination}
            onPaginationChange={updatePagination}
            manualPagination
            manualSorting
            showSelectionCount
            getRowId={(row) => row._id}
          />
        </div>
      </CardContent>
      <SendTicketsDialog
        open={sendTicketsDialogOpen}
        onOpenChange={setSendTicketsDialogOpen}
        selectedParents={selectedParents}
      />
      <BulkAddTagDialog
        open={bulkAddTagDialogOpen}
        onOpenChange={setBulkAddTagDialogOpen}
        selectedParents={selectedParents}
        onSuccess={() => {
          setBulkAddTagDialogOpen(false);
          setSelectedParents([]);
          setRowSelection({});
          selectedParentsByIdRef.current = {};
        }}
      />
      <BulkSyncTagFromCrmDialog
        open={bulkSyncTagFromCrmOpen}
        onOpenChange={setBulkSyncTagFromCrmOpen}
      />
      <DeleteParentDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        selectedParents={selectedParents}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedParents([]);
          setRowSelection({});
          selectedParentsByIdRef.current = {};
          setSelectAllMatching(false);
        }}
        isBulkDelete={selectAllMatching || selectedParents.length > 1}
        isSelectAllMatching={selectAllMatching}
        matchingCount={totalMatching}
        onDelete={() => {
          if (selectAllMatching) {
            bulkDeleteMutation.mutate(
              {
                filters: {
                  search: filters.query || undefined,
                  tags: tagFilter,
                },
              },
              {
                onSuccess: reportBulkDeleteResult,
                onError: () => {
                  toast.error('Failed to delete parents');
                },
              },
            );
          } else {
            bulkDeleteMutation.mutate(
              {
                parentIds: selectedParents.map((parent) => parent._id),
              },
              {
                onSuccess: reportBulkDeleteResult,
                onError: () => {
                  toast.error('Failed to delete parents');
                },
              },
            );
          }
        }}
      />
    </Card>
  );
}
